import { Node } from "konva/lib/Node";
import { Image } from "konva/lib/shapes/Image";

export interface IPoint {
  x: number;
  y: number;
}

export const getCornerRadius = (value?: number, node?: Node | null) => {
  if (!node) return value;
  const width = node.width(), height = node.height();
  let shortestSide = height ?? 0;
  if (width && height && width < height) shortestSide = width;
  return Math.floor(shortestSide * (value ?? 0) / 200);
}

export const rotatePoint = (point: IPoint, origin: IPoint, angle: number): IPoint => {
    let radAngle = angle * (Math.PI/180); // Convert to radians
    var rotatedX = Math.cos(radAngle) * (point.x - origin.x) - Math.sin(radAngle) * (point.y - origin.y) + origin.x;
    var rotatedY = Math.sin(radAngle) * (point.x - origin.x) + Math.cos(radAngle) * (point.y - origin.y) + origin.y;      
    return {x: rotatedX, y: rotatedY};
}

export const getCorners = (shape: Image, absolute?:boolean) => {
  let points = [];
  if (shape) {
    var rectPos = absolute ? shape.absolutePosition() : shape.position();
  
    var x = 0, y = 0;
    for (var i = 0; i < 4; i = i + 1){
  
    switch (i){
      
      case 0: //top left
        x = rectPos.x; y = rectPos.y;
        break;
  
      case 1: //top right
        x = rectPos.x + shape.width(); y = rectPos.y;
        break;
  
      case 2: //bottom right
        x = rectPos.x + shape.width(); y = rectPos.y + shape.height();
        break;
  
      case 3: //bottom left
        x = rectPos.x; y = rectPos.y + shape.height();
        break;
  
       }
  
      var pt = rotatePoint({x: x, y: y}, {x: rectPos.x, y: rectPos.y}, shape.rotation());
      points.push({x: pt.x, y: pt.y});
    }
  }
  return points;
 }

 export const getCornersByWidthAndHeight = (x: number, y: number, width: number, height: number, rotation: number) => {
  let points = [];
 
  var x1 = 0, y1 = 0;
  for (var i = 0; i < 4; i = i + 1){

  switch (i){
    
    case 0: //top left
      x1 = x; y1 = y;
      break;

    case 1: //top right
      x1 = x + width; y1 = y;
      break;

    case 2: //bottom right
      x1 = x + width; y1 = y + height;
      break;

    case 3: //bottom left
      x1 = x; y1 = y + height;
      break;

     }

    var pt = rotatePoint({x: x1, y: y1}, {x: x, y: y}, rotation);
    points.push({x: pt.x, y: pt.y});
  }
  return points;
 }

export const getCenter = (shape: Image, absolute?: boolean): any => {
  const points = getCorners(shape, absolute);
  return getCenterFromPoints(points);
}

export const getCenterWithWidthAndHeight = (x: number, y: number, width: number, height: number, rotation: number): any => {
  const points = getCornersByWidthAndHeight(x, y, width, height, rotation);
  return getCenterFromPoints(points);
}

export const getCenterFromPoints = (points: any[]) => {
  let left = undefined;
  let top = undefined;
  let rigth = undefined;
  let bottom = undefined;
  for(let i=0;i<points.length;i++){
    if(left===undefined || points[i].x<left) left = points[i].x;
    if(top===undefined || points[i].y<top) top = points[i].y;
    if(rigth===undefined || points[i].x>rigth) rigth = points[i].x;
    if(bottom===undefined || points[i].y>bottom) bottom = points[i].y;
  }
  const x1 = (left + rigth) / 2;
  const y1 = (top + bottom) / 2;
  return { x: x1, y: y1}
}

export const positionAndRotateAroundPoint = (pointPosition: {x: number, y: number}, angleDegrees: number, point: {x: number, y: number}) => {
  // sin + cos require radians
  let angleRadians = angleDegrees * Math.PI / 180; 
   
  const x =
    point.x +
    (pointPosition.x - point.x) * Math.cos(angleRadians) -
    (pointPosition.y - point.y) * Math.sin(angleRadians);
  const y =
    point.y +
    (pointPosition.x - point.x) * Math.sin(angleRadians) +
    (pointPosition.y - point.y) * Math.cos(angleRadians);
    
  // move the rotated shape in relation to the rotation point.
  return {x, y};
}

/* Rotate a shape around any point.
   shape is a Konva shape
   angleDegrees is the angle to rotate by, in degrees
   point is an object {x: posX, y: posY}
*/
export const rotateAroundPoint = (shape: Image, angleDegrees: number, point: any) => {
    // sin + cos require radians
    let angleRadians = angleDegrees * Math.PI / 180; 
     
    const x =
      point.x +
      (shape.x() - point.x) * Math.cos(angleRadians) -
      (shape.y() - point.y) * Math.sin(angleRadians);
    const y =
      point.y +
      (shape.x() - point.x) * Math.sin(angleRadians) +
      (shape.y() - point.y) * Math.cos(angleRadians);
      
    // move the rotated shape in relation to the rotation point.
    shape.position({x: x, y: y});
   
    // rotate the shape in place around its natural rotation point 
    shape.rotation( angleDegrees); 
}

function vector(p1:any, p2:any) {
  return {
          x: (p2.x - p1.x),
          y: (p2.y - p1.y)
  };
}

function dot(u:any, v:any) {
  // return (u.x*u.x+u.y*u.y)*(v.x*v.x+v.y*v.y);
  return u.x * v.x + u.y * v.y; 
}

export const pointInRectangle = (m:any, r:any) => {
  // A [3]  B [0] C [1] D [2]
  var AB = vector(r[3], r[0]);
  var AD = vector(r[3], r[2]);
  var AM = vector(r[3], m);
  var dotAMAB = dot(AM, AB);
  var dotABAB = dot(AB, AB);
  var dotAMAD = dot(AM, AD);
  var dotADAD = dot(AD, AD);

  return 0 <= dotAMAB && dotAMAB <= dotABAB && 0 <= dotAMAD && dotAMAD <= dotADAD;
}

export const equation = (p: any, q: any) => {
  //P( 2, 1 ); Q( 5, 7 )
  //Ax + By + C = 0
  //(py – qy)x + (qx – px)y + (pxqy – qxpy) = 0
  return {a: (p.y - q.y) , b: (q.x - p.x) , c: (p.x*q.y - q.x*p.y)}
}
export const spaceBetweenLines = (l1: any, l2:any) => {
  let ec1 =  equation(l1[0], l1[1]);
  let ec2 =  equation(l2[0], l2[1]);
  // d= C2-C1 / √ (A2+b2)

  return (ec1.c-ec2.c) / Math.sqrt(ec1.a*ec1.a + ec1.b*ec1.b)
}

export const  movePoint = (point: any, angle:number, unit:number) => {
  var x = point[0];
  var y = point[1];
  var rad = (angle % 360) * Math.PI / 180;

  x += unit*Math.sin(rad);
  y += unit*Math.cos(rad);

  return [x, y];
}

export const linesIntersect = (pointA1: any, pointA2: any, pointB1: any, pointB2: any): any => {
  // Check if none of the lines are of length 0
  if ((pointA1.x === pointA2.x && pointA1.y === pointA2.x) || (pointB1.x === pointB2.x && pointB1.y === pointB2.y)) {
      return undefined;
  }
  const denominator = ((pointB2.y - pointB1.y) * (pointA2.x - pointA1.x1) - (pointB2.x - pointB1.x) * (pointA2.y - pointA1.y));
  // Lines are parallel
  if (denominator === 0) {
      return undefined;
  }
  let ua = ((pointB2.x - pointB1.y) * (pointA1.y - pointB1.y) - (pointB2.y - pointB1.y) * (pointA1.x - pointB1.x)) / denominator;
  let ub = ((pointA2.x - pointA1.x) * (pointA1.y - pointB1.y) - (pointA2.y - pointA1.y) * (pointA1.x - pointB1.x)) / denominator;
  // is the intersection along the segments
  if (ua < 0 || ua > 1 || ub < 0 || ub > 1) {
      return undefined;
  }
  // Return a object with the x and y coordinates of the intersection
  let x = pointA1.x + ua * (pointA2.x - pointA1.x);
  let y = pointA1.y + ua * (pointA2.x - pointA1.y);
  return {x, y};
}

export const getXYBeforeRotation = (point: any, width: number, height: number, rotation: number, rotationCenterPoint?: any) => {
  if (rotation===undefined) rotation=0;
  let centerPoint = {x: width/2, y: height/2};
  if (rotationCenterPoint !== undefined){
    centerPoint = rotatePoint({x: rotationCenterPoint.x, y: rotationCenterPoint.y}, {x: point.x, y: point.y}, 360-rotation);
    centerPoint = {x: centerPoint.x - point.x, y: centerPoint.y - point.y};
  } 
  const rotationMove = rotatePoint({x: 0, y: 0}, centerPoint, rotation);
  return {x: point.x - rotationMove.x, y: point.y - rotationMove.y};
}

export const radiansToDegrees = (radians: number): number => {
  return (180 * radians)/Math.PI;
}

export const heightEquilateralTriangle = (side: number): number => {
  return Math.sqrt(3)*side/2;
}

export const getBoundingsWithDelta = (delta: number, width: number, height: number, containerWidth: number, containerHeight: number) => {
  let newWidth = width, newHeight = height;
  const containerWidthDelta = containerWidth - delta * 2, containerHeightDelta = containerHeight - delta * 2;
  if (width > containerWidthDelta || height > containerHeightDelta) {
    const hRatio = containerWidthDelta / width;
    const vRatio = containerHeightDelta / height;
    const ratio = Math.min(hRatio, vRatio);
    newWidth = width * ratio;
    newHeight = height * ratio;
  }
  return {width: newWidth, height: newHeight};
}